import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { selectBoardId } from '../../redux/Slices/boardIdSlice';
import ZoobbeSelect from '../Global/ZoobbeSelect';
import Spinner from '../Global/Spinner';

import { togglePopover } from '../../redux/Slices/popoverSlice';
import { fetchMembers, memberInfo, setBoardMembersFromSocket } from '../../redux/Slices/memberSlice';
import { config } from '../../config';

import ImagePlaceholder from '../Global/ImagePlaceholder';
import { isValidEmail, debounce } from '../../utils/helpers';

import './scss/ShareBoard.scss';
import '../Global/scss/MembersSearchResult.scss';
import useOutsideClick from '../../hooks/useOutsideClick';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import MemberOnlineStatus from '../Global/MemberOnlineStatus';
import { ActionTypes } from '../../sockets/ActionTypes';
import { useSocketAction } from '../../hooks/useSocketManager';
import { Close } from '../icons';
import { useWhiteLabel } from '../../context/WhiteLabelContext';

const ShareBoardModal = ({ workspaceSlug }) => {
    const dispatch = useDispatch();
    const { getAppName } = useWhiteLabel();
    const appName = getAppName();

    const [email, setEmail] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [message, setMessage] = useState("We're excited to have you on board. Let’s get started!");
    const [activeTab, setActiveTab] = useState('members');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedRole, setSelectedRole] = useState({ value: 'member', label: 'Member' });
    const [popoverState, setPopoverState] = useState({ isVisible: false, contentId: null });
    const [size] = useState(35);
    const [isFocused, setIsFocused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchingUsers, setSearchingUsers] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState({});
    const [openMemberId, setOpenMemberId] = useState(null); // Track which member is open

    const emailInputRef = useRef(null);
    const emailRef = useRef();

    const { board } = useSelector((state) => state.board);
    const [inviteLink, setInviteLink] = useState(board?.joinLink);

    const { user } = useSelector((state) => state.user);

    const boardId = useSelector(selectBoardId);

    const collaborators = useSelector(state => state.member.boardMembers);

    const totalAdmins = collaborators.filter(m => m.role === 'admin').length;

    const { workspaceMembers } = useSelector(state => state.member);

    // Check if current user is a board admin or workspace admin
    const currentUserBoardMember = collaborators.find(m => m._id === user?.user?._id);
    const isBoardAdmin = currentUserBoardMember?.role === 'admin';
    const isWorkspaceAdmin = currentUserBoardMember?.workspaceRole === 'admin';

    const { handlePopoverClick } = useHandlePopoverClick();

    useEffect(() => {
        if (boardId) {
            dispatch(fetchMembers({ type: 'board', id: boardId }));
        }
    }, [boardId, dispatch]);

    // Real-time socket listener for role updates and member removal
    useSocketAction([
        ActionTypes.ROLE_UPDATED,
        ActionTypes.MEMBER_REMOVED,
        ActionTypes.MEMBER_ADDED,
    ], (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        // Only process events for this board
        if (data.typeId !== boardId && data.boardId !== boardId) return;

        console.log('[ShareBoard] Real-time member event:', data.action, data);

        // ✅ Use members from socket event (optimistic update)
        if (data.members && Array.isArray(data.members)) {
            // Update Redux members state directly from socket data
            dispatch(setBoardMembersFromSocket(data.members));
        } else {
            // Fallback: fetch if members not included in socket event
            console.warn('[ShareBoard] Members not in socket event, fetching...');
            dispatch(fetchMembers({ type: 'board', id: boardId }));
        }
    }, [dispatch, boardId, user?.user?._id]);

    useEffect(() => {
        if (emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, []);

    // Debounced search function
    const searchUsers = useRef(
        debounce(async (searchQuery) => {
            if (!searchQuery || searchQuery.trim().length === 0) {
                setSearchResults([]);
                setSearchingUsers(false);
                return;
            }

            setSearchingUsers(true);
            try {
                const response = await fetch(
                    `${config.API_URI}/v1/users/all-users?search=${encodeURIComponent(searchQuery)}&limit=10`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    const users = data.users || [];

                    // Show email as an invite option if no users are found and input is a valid email format
                    if (users.length === 0 && isValidEmail(searchQuery)) {
                        setSearchResults([{ email: searchQuery, isEmailInvite: true }]);
                    } else {
                        setSearchResults(users);
                    }
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Error searching users:', error);
                setSearchResults([]);
            } finally {
                setSearchingUsers(false);
            }
        }, 300)
    ).current;

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        if (value?.length > 0) {
            searchUsers(value);
        } else {
            setSearchResults([]);
            setSearchingUsers(false);
        }
    };


    const handleSelectUser = (user) => {
        if (!selectedUsers.some((selected) => selected._id === user._id)) {
            // If the selected option is an existing user
            setSelectedUsers([...selectedUsers, user]);
        }
        else {
            setSelectedUsers([...selectedUsers, { email: user.email, isEmailInvite: true }]);
        }
        setEmail('');
        setSearchResults([]);
    };

    const removeUser = (index) => {
        setSelectedUsers((prev) => prev.filter((_, i) => i !== index));
    };

    const addMemberToBoard = async (e, selectedWorkspaceMembers) => {
        e.preventDefault();

        setLoading(true);

        if (!boardId) {
            showError(dispatch, 'Board Id Empty');
            setLoading(false);
            return;
        }

        try {


            // Prepare the members array with both memberId and role
            const members = selectedWorkspaceMembers.map((user) => ({
                memberId: user._id || null,
                email: user.email || null,
                role: selectedRole.value || 'member',
            }));



            const response = await fetch(config.API_URI + `/v1/boards/${boardId}/member`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({
                    boardId: boardId,
                    members: members,
                    message: message
                }),
            });

            const data = await response.json();

            if (response.status === 200) {
                dispatch(fetchMembers({ type: 'board', id: boardId }));
                showSuccess(dispatch, data.message);
                setLoading(false);
                setSelectedUsers([]);
            }
            else {
                showError(dispatch, data.message);
                setLoading(false);
            }
        } catch (error) {
            console.error('Error adding member:', error);
            showError(dispatch, 'Error adding member');
        }
    };

    const handleInviteByEmail = async (e, selectedNonWorkspaceMembers) => {
        e.preventDefault();

        if (selectedNonWorkspaceMembers.length === 0) {
            setLoading(false);
            showError(dispatch, 'Please select at least one user');
            return;
        }

        const memberId = selectedNonWorkspaceMembers[0]._id; // Assuming you're sending one invitee for re-invite

        setLoadingUsers(prev => ({ ...prev, [memberId]: true }));

        try {

            const invitees = selectedNonWorkspaceMembers.map((user) => ({
                email: user.email,
                role: selectedRole.value || 'member',
            }));

            const response = await fetch(`${config.API_URI}/v1/boards/${boardId}/invite`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ invitees, message }),
            });

            const data = await response.json();

            if (response.ok) {
                dispatch(fetchMembers({ type: 'board', id: boardId }));
                showSuccess(dispatch, 'Invitations sent!');
                setSelectedUsers([]);
                setLoading(false);
            } else {
                showError(dispatch, data.message);
            }
        } catch (error) {
            showError(dispatch, 'Failed to send invite');
        } finally {
            setLoadingUsers(prev => ({ ...prev, [memberId]: false }));
        }
    };

    const handleGenerateJoinLink = async () => {
        try {


            const response = await fetch(`${config.API_URI}/v1/boards/${boardId}/generate-join-link`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
            });

            const data = await response.json();
            if (response.ok) setInviteLink(data?.joinLink);
            else showError(dispatch, data.message);
        } catch (error) {
            showError(dispatch, 'Failed to generate join link');
        }
    };

    const handleDeleteJoinLink = async () => {
        try {


            const response = await fetch(`${config.API_URI}/v1/boards/${boardId}/delete-join-link`, {
                method: 'DELETE',

                credentials: 'include',
            });

            if (response.ok) {
                setInviteLink(null);
                showSuccess(dispatch, 'Join link deleted');
            } else {
                const data = await response.json();
                showError(dispatch, data.message);
            }
        } catch (error) {
            showError(dispatch, 'Failed to delete join link');
        }
    };


    const handleMembersShare = async (e) => {
        e.preventDefault();

        setLoading(true);

        if (selectedUsers.length === 0) {
            setLoading(false);
            showError(dispatch, 'Please select at least one user');
            return;
        }

        const selectedWorkspaceMembers = selectedUsers.filter(user =>
            workspaceMembers.some(member => member._id === user._id)
        );

        const selectedNonWorkspaceMembers = selectedUsers.filter(user =>
            !workspaceMembers.some(member => member._id === user._id)
        );


        if (selectedWorkspaceMembers.length > 0) {
            // Add members who are part of the workspace to the board
            await addMemberToBoard(e, selectedWorkspaceMembers);
        }

        {/* Will support it letter */ }

        // if (selectedNonWorkspaceMembers.length > 0) {
        //     // Send invitation emails to users who are not part of the workspace
        //     await handleInviteByEmail(e, selectedNonWorkspaceMembers);
        // }

        if (selectedNonWorkspaceMembers.length > 0) {
            // Send invitation emails to users who are not part of the workspace
            await addMemberToBoard(e, selectedNonWorkspaceMembers);
        }
    };

    const handleRoleSelect = (role) => {
        if (role.value === 'remove') return;
        setSelectedRole(role);
    };

    const handleMemberInfo = (e, contentId, member) => {
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        const position = { top: rect.bottom, left: rect.left };

        if (popoverState.isVisible && popoverState.contentId === contentId) {
            setPopoverState({ isVisible: false, contentId: null });
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
        } else {
            setPopoverState({ isVisible: true, contentId });
            dispatch(togglePopover({ contentId, position, targetId: target.id }));
            dispatch(memberInfo({ member }));
        }
    };

    const roleOptions = [
        { value: 'admin', label: 'Admin' },
        { value: 'member', label: 'Member' },
        // { value: 'observer', label: 'Observer' },
    ];

    const handleFocus = () => {
        setIsFocused(true); // Set focus to true
    };

    useOutsideClick(emailRef, () => {
        setIsFocused(false);
        setOpenMemberId(null);

    });

    const handeReInvite = async (e, member) => {
        e.preventDefault();

        setLoadingUsers(prev => ({ ...prev, [member._id]: true }));

        try {
            await handleInviteByEmail(e, [member]);
        } finally {
            setLoadingUsers(prev => ({ ...prev, [member._id]: false }));
        }
    };

    const handleCopyLink = () => {
        if (inviteLink) {
            navigator.clipboard.writeText(inviteLink);
            showSuccess(dispatch, 'Join link copied to clipboard!');
        }
    };


    return (
        <div className="sbm-share-board-modal">
            <div className="sbm-header">
                <h3>Share Board</h3>
            </div>
            <form onSubmit={handleMembersShare}>
                <div className="sbm-input-group">
                    <div
                        className="sbm-email-input"
                        ref={emailRef}
                        style={{
                            outline: isFocused ? '1px solid var(--brand-color)' : '1px solid var(--input-border-color)',
                        }}
                    >
                        {selectedUsers.map((selectedUser, index) => (
                            <div key={index} className="sbm-selected-email">
                                {`${selectedUser.name || selectedUser.email}`}
                                <button type="button" onClick={() => removeUser(index)}>
                                    <Close size={16} />
                                </button>
                            </div>
                        ))}
                        <input
                            ref={emailInputRef}
                            type="text"
                            value={email}
                            onChange={handleEmailChange}
                            placeholder="Type name or email"
                            autoComplete="off"
                            autoCapitalize="none"
                            spellCheck="false"
                            onFocus={handleFocus}
                        />
                        {
                            searchingUsers ? (
                                <div className="sbm-search-results">
                                    <div className='no-user-message' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Spinner size={16} color='var(--text-color)' strokeWidth={7} />
                                        Searching...
                                    </div>
                                </div>
                            ) : isFocused && email && searchResults.length > 0 ? (
                                <div className="sbm-search-results">
                                    {searchResults.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            className="mention-suggestion"
                                            onClick={() => handleSelectUser(suggestion)}
                                        >
                                            {suggestion.isEmailInvite ? (
                                                <>
                                                    <ImagePlaceholder key={suggestion._id} size={size} text={suggestion.email} />
                                                    <div>
                                                        <div className="suggession-name">{suggestion.email}</div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {suggestion.profilePicture ? (
                                                        <img src={suggestion.profilePicture} alt={suggestion.name} loading="lazy" />
                                                    ) : (
                                                        <ImagePlaceholder key={suggestion._id} size={size} text={suggestion.username} onlineIndicator={false} />
                                                    )}
                                                    <div>
                                                        <div className="suggession-name">{suggestion.name}</div>
                                                        <div className="suggession-handler">{`@${suggestion.username}`}</div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Show the "no user found" message only when:
                                // 1. User has typed something (email exists)
                                // 2. Not currently searching
                                // 3. Search results are empty
                                // 4. Input is not a valid email format
                                isFocused && email && !searchingUsers && searchResults.length === 0 && !isValidEmail(email) && (
                                    <div className="sbm-search-results">
                                        <div className='no-user-message'>
                                            Looks like that person isn't a {appName} member yet. Add their email address to invite them.
                                        </div>
                                    </div>
                                )
                            )
                        }



                    </div>
                    <ZoobbeSelect
                        options={roleOptions}
                        defaultSelectedOption={1}
                        onSelect={handleRoleSelect}
                    />
                </div>
                <button type="submit" className={`sbm-invite-button${loading ? ' disabled' : ''}`} disabled={loading}>
                    {loading ? <Spinner size={20} color='#fff' strokeWidth={7} /> : 'Share'}
                </button>
            </form>
            <textarea
                className="sbm-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
            />
            <div className="sbm-invite-link">
                {inviteLink ? (
                    <div className="invite-link-info">
                        <span className="material-symbols-outlined">
                            link
                        </span>
                        <span className="invite-link-text">Anyone with the link can join as a member</span>
                        <span className="invite-link-actions">
                            <button onClick={handleCopyLink} className="link-action">Copy link</button> •
                            <button onClick={handleDeleteJoinLink} className="link-action">Delete link</button>
                        </span>
                    </div>
                ) : (
                    <div className="invite-link-info">
                        <span className="material-symbols-outlined">
                            link
                        </span>
                        <span className="invite-link-text">Share this board with a link</span>
                        <span className="invite-link-actions">
                            <button onClick={handleGenerateJoinLink} className="link-action">Create link</button>
                        </span>
                    </div>
                )}
            </div>
            <div className="sbm-tabs">
                <button
                    className={`sbm-tab ${activeTab === 'members' ? 'active' : ''}`}
                    onClick={() => setActiveTab('members')}
                >
                    Board members
                </button>
                <button
                    className={`sbm-tab ${activeTab === 'requests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('requests')}
                >
                    Requests
                </button>
            </div>
            {activeTab === 'members' && (
                <div className="sbm-workspace-members">
                    {collaborators?.map((member, index) => {

                        if (!member?._id) return;

                        const isCurrentUser = member._id === user?.user?._id;

                        return (
                            <div className="sbm-member" key={index + ' ' + member._id}>
                                <span className="avatar" onClick={(e) => { handleMemberInfo(e, 'memberInfo', member) }} data-popover-trigger>
                                    {member.profilePicture ? (

                                        <div className="image-placeholder-container">
                                            <img
                                                className="sbm-member-image"
                                                style={{
                                                    width: size,
                                                    height: size,
                                                    filter: member.userStatus === 'inactive' ? 'grayscale(100%)' : 'none'
                                                }}
                                                src={member.profilePicture}
                                                alt={member.name}
                                                loading="lazy"
                                            />
                                            {member.userStatus === 'inactive' && (
                                                <span className="inactive-indicator" title="Inactive user">
                                                    <span className="material-symbols-outlined">schedule</span>
                                                </span>
                                            )}
                                            {member.userStatus !== 'inactive' && <MemberOnlineStatus member={member} />}
                                        </div>
                                    ) : (
                                        <ImagePlaceholder member={member} size={size} text={member.username} onlineIndicator={true} />
                                    )}
                                </span>
                                <div className="shared-members-name">
                                    <span className="sbm-member-name">
                                        {member.name} {isCurrentUser && '(you)'}
                                    </span>

                                    <span className="member-username">@{member.username} • {member.workspaceRole === 'guest' ? 'WorkSpace Guest' : `WorkSpace ${member.workspaceRole}`}</span>

                                    {member.userStatus === 'inactive' && (
                                        <span className="member-status inactive">Not yet joined</span>
                                    )}
                                </div>

                                {member.userStatus === 'inactive' && (isBoardAdmin || isWorkspaceAdmin) && (
                                    <div
                                        className={`invite-ghost-user${loadingUsers[member._id] ? ' spin-loading' : ''}`}
                                        data-tooltip-content='Send Invitation'
                                        data-tooltip-position='top'
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            setLoadingUsers(prev => ({ ...prev, [member._id]: true }));
                                            try {
                                                const response = await fetch(`${config.API_URI}/v1/fluent/invite-members`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    credentials: 'include',
                                                    body: JSON.stringify({
                                                        boardId,
                                                        memberEmails: [member.email],
                                                        message: `You've been invited to join the board.`
                                                    })
                                                });
                                                if (response.ok) {
                                                    showSuccess(dispatch, `Invitation sent to ${member.email}`);
                                                } else {
                                                    showError(dispatch, 'Failed to send invitation');
                                                }
                                            } catch (err) {
                                                showError(dispatch, 'Failed to send invitation');
                                            } finally {
                                                setLoadingUsers(prev => ({ ...prev, [member._id]: false }));
                                            }
                                        }}
                                    >
                                        {loadingUsers[member._id] ? (
                                            <Spinner size={18} color="#333" strokeWidth={7} />
                                        ) : (
                                            <span className="material-symbols-outlined">send</span>
                                        )}
                                    </div>
                                )}

                                <div
                                    className={`members-options ${openMemberId === member._id ? 'active' : ''}`}
                                    id={`popover-member-${member._id}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenMemberId(openMemberId === member._id ? null : member._id);
                                        handlePopoverClick(e, 'memberRole', {
                                            memberId: member._id,
                                            currentRole: member.role,
                                            isClosed: false,
                                            boardId,
                                            memberDeleteTarget: `popover-member-${member._id}`, // Pass ID instead of the event
                                            totalAdmins,
                                            apiPath: 'board',
                                            typeId: boardId,
                                            canChangeRole: isBoardAdmin || isWorkspaceAdmin, // Only admins can change roles
                                            isWorkspaceAdmin, // Workspace admins can bypass last-admin restriction
                                        });
                                    }}
                                    data-popover-trigger
                                >
                                    {member.role}
                                    <span className="material-symbols-outlined">keyboard_arrow_down</span>
                                </div>


                            </div>
                        )
                    })}
                </div>
            )}
            {activeTab === 'requests' && (
                <div className="sbm-requests">No requests found</div>
            )}
        </div>
    );
};

export default ShareBoardModal;
